// apps/platform/src/components/ActivityTypeDrawer.tsx
//
// Modal for create / edit of activity_types. Backed by `write-activity-type`
// edge fn. Once the type is saved (or when editing an existing type), the
// embedded ExtraFieldEditor reveals so admins can manage per-type extra
// fields in the same surface.
//
// base_behavior is immutable post-creation per Key Technical Decisions —
// the field renders as read-only when editing.

import { useEffect, useState, type CSSProperties } from "react";
import {
  createActivityType,
  updateActivityType,
  listExtraFields,
  type ActivityType,
  type ActivityTypeExtraField,
  type BaseBehavior,
} from "../lib/activityTypesClient";
import { ColorPickerField } from "./editor/ColorPickerField";
import { ExtraFieldEditor } from "./ExtraFieldEditor";
import { colors, fontSize, fontWeight, radius, shadow, space } from "../lib/tokens";

const BASE_BEHAVIORS: Array<{ value: BaseBehavior; label: string; help: string }> = [
  { value: "internal", label: "Interno",  help: "Em um espaço da casa — usa SpacePicker + check-in." },
  { value: "external", label: "Externo",  help: "Endereço livre fora da casa — sem check-in por padrão." },
  { value: "trip",     label: "Viagem",   help: "Múltiplos dias — destino livre, datas sem hora." },
  { value: "online",   label: "Online",   help: "Encontro virtual — exige URL da chamada." },
];

const SLUG_RE = /^[a-z0-9][a-z0-9-]*$/;

interface FormState {
  slug: string;
  label: string;
  base_behavior: BaseBehavior;
  icon: string;
  color: string;
  enabled: boolean;
}

const EMPTY_FORM: FormState = {
  slug: "",
  label: "",
  base_behavior: "internal",
  icon: "",
  color: "#3D4EE8",
  enabled: true,
};

function fromActivityType(t: ActivityType): FormState {
  return {
    slug: t.slug,
    label: t.label,
    base_behavior: t.base_behavior,
    icon: t.icon ?? "",
    color: t.color ?? "#3D4EE8",
    enabled: t.enabled,
  };
}

function slugify(input: string): string {
  return input
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function validate(form: FormState): string | null {
  if (!form.label.trim()) return "Rótulo obrigatório.";
  if (!SLUG_RE.test(form.slug)) return "slug deve casar com ^[a-z0-9][a-z0-9-]*$";
  if (!BASE_BEHAVIORS.some((b) => b.value === form.base_behavior)) return "base_behavior inválido.";
  return null;
}

interface ActivityTypeDrawerProps {
  tenantId: string;
  type: ActivityType | null; // null = create mode
  onClose: () => void;
  onSaved: () => void;
}

export function ActivityTypeDrawer({ tenantId, type, onClose, onSaved }: ActivityTypeDrawerProps) {
  const isEdit = !!type;
  // Local copy of the type so we can transition from "new" → "edit" without
  // closing the drawer after the first save.
  const [activeType, setActiveType] = useState<ActivityType | null>(type);
  const [form, setForm] = useState<FormState>(type ? fromActivityType(type) : EMPTY_FORM);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [slugTouched, setSlugTouched] = useState(isEdit);

  const [fields, setFields] = useState<ActivityTypeExtraField[]>([]);
  const [fieldsLoading, setFieldsLoading] = useState(false);

  const refreshFields = async (activityTypeId: string) => {
    setFieldsLoading(true);
    try {
      const rows = await listExtraFields(activityTypeId);
      setFields(rows);
    } catch (err) {
      console.error("[ActivityTypeDrawer] listExtraFields failed", err);
    } finally {
      setFieldsLoading(false);
    }
  };

  useEffect(() => {
    if (activeType) {
      refreshFields(activeType.id);
    }
  }, [activeType?.id]);

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => {
      const next = { ...prev, [key]: value };
      if (key === "label" && !slugTouched) {
        next.slug = slugify(value as string);
      }
      return next;
    });

  const handleSaveType = async () => {
    const err = validate(form);
    if (err) { setError(err); return; }
    setSaving(true);
    setError(null);

    const payload = {
      slug: form.slug.trim(),
      label: form.label.trim(),
      icon: form.icon.trim() || null,
      color: form.color.trim() || null,
      enabled: form.enabled,
    };

    const res = activeType
      ? await updateActivityType(activeType.id, payload)
      : await createActivityType(tenantId, { ...payload, base_behavior: form.base_behavior });

    setSaving(false);
    if (res.error || !res.row) {
      setError(res.error || "Falha ao salvar tipo.");
      return;
    }
    setActiveType(res.row);
    onSaved();
  };

  return (
    <div style={s.backdrop}>
      <div style={s.card}>
        <div style={s.header}>
          <div>
            <h2 style={s.title}>{activeType ? "Editar tipo de atividade" : "Novo tipo de atividade"}</h2>
            {activeType?.deprecated_at && (
              <p style={s.deprecatedNote}>Descontinuado em {new Date(activeType.deprecated_at).toLocaleDateString("pt-BR")}.</p>
            )}
          </div>
          <button onClick={onClose} aria-label="Fechar" style={s.closeBtn}>✕</button>
        </div>

        <section style={s.section}>
          <div style={s.sectionLabel}>Metadados</div>
          <div style={s.fieldGrid}>
            <label style={s.label}>
              <span style={s.labelText}>Rótulo <span style={s.required}>*</span></span>
              <input
                style={s.input}
                value={form.label}
                onChange={(e) => update("label", e.target.value)}
                placeholder="Ex: Retiro Espiritual"
              />
            </label>
            <label style={s.label}>
              <span style={s.labelText}>slug <span style={s.required}>*</span></span>
              <input
                style={s.input}
                value={form.slug}
                onChange={(e) => { setSlugTouched(true); update("slug", e.target.value); }}
                placeholder="retiro-espiritual"
              />
            </label>
            <label style={{ ...s.label, gridColumn: "1 / -1" }}>
              <span style={s.labelText}>Comportamento base <span style={s.required}>*</span></span>
              {activeType ? (
                <div style={s.readOnlyBox}>
                  <strong>{BASE_BEHAVIORS.find((b) => b.value === form.base_behavior)?.label}</strong>
                  <span style={s.readOnlyHint}> · imutável após criação. Para mudar, descontinue este tipo e crie outro.</span>
                </div>
              ) : (
                <div style={s.radioGrid}>
                  {BASE_BEHAVIORS.map((b) => (
                    <label key={b.value} style={form.base_behavior === b.value ? { ...s.radioCard, ...s.radioCardActive } : s.radioCard}>
                      <input
                        type="radio"
                        name="base_behavior"
                        value={b.value}
                        checked={form.base_behavior === b.value}
                        onChange={() => update("base_behavior", b.value)}
                        style={{ marginRight: space.sm }}
                      />
                      <span style={s.radioTitle}>{b.label}</span>
                      <span style={s.radioHelp}>{b.help}</span>
                    </label>
                  ))}
                </div>
              )}
            </label>
            <label style={s.label}>
              <span style={s.labelText}>Ícone (lucide slug)</span>
              <input
                style={s.input}
                value={form.icon}
                onChange={(e) => update("icon", e.target.value)}
                placeholder="ex: tent · plane · video"
              />
            </label>
            <div>
              <ColorPickerField
                label="Cor"
                value={form.color}
                onChange={(c) => update("color", c)}
              />
            </div>
            <label style={{ ...s.label, justifyContent: "center" }}>
              <span style={s.checkboxRow}>
                <input
                  type="checkbox"
                  checked={form.enabled}
                  onChange={(e) => update("enabled", e.target.checked)}
                />
                <span>Habilitado (aparece para o admin do tenant)</span>
              </span>
            </label>
          </div>

          {error && <div style={s.errorBox}>{error}</div>}

          <div style={s.formActions}>
            <button onClick={onClose} disabled={saving} style={s.cancelBtn}>
              {activeType ? "Fechar" : "Cancelar"}
            </button>
            <button onClick={handleSaveType} disabled={saving} style={s.submitBtn}>
              {saving ? "Salvando..." : activeType ? "Atualizar tipo" : "Criar tipo"}
            </button>
          </div>
        </section>

        {activeType && (
          <section style={s.section}>
            <div style={s.sectionLabel}>Estrutura</div>
            {fieldsLoading ? (
              <div style={s.empty}>Carregando campos...</div>
            ) : (
              <ExtraFieldEditor
                tenantId={tenantId}
                activityTypeId={activeType.id}
                fields={fields}
                onChanged={() => refreshFields(activeType.id)}
              />
            )}
          </section>
        )}
      </div>
    </div>
  );
}

const s: Record<string, CSSProperties> = {
  backdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(14, 17, 22, 0.4)",
    zIndex: 1000,
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "center",
    padding: space.lg,
    overflowY: "auto",
  },
  card: {
    background: colors.white,
    border: `1px solid ${colors.fog}`,
    borderRadius: radius.md,
    boxShadow: shadow.lg,
    padding: space.xxl,
    width: 720,
    maxWidth: "100%",
    marginTop: space.xxl,
    marginBottom: space.xxl,
  },
  header: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    marginBottom: space.lg,
  },
  title: {
    margin: 0,
    fontSize: fontSize.xl,
    fontWeight: fontWeight.semibold,
    color: colors.ink,
  },
  closeBtn: {
    background: "transparent",
    border: "none",
    color: colors.stone,
    fontSize: fontSize.lg,
    cursor: "pointer",
    padding: 4,
  },
  deprecatedNote: {
    margin: `${space.xs}px 0 0 0`,
    fontSize: fontSize.sm,
    color: colors.error,
  },
  section: {
    marginBottom: space.xxl,
    paddingBottom: space.lg,
    borderBottom: `1px solid ${colors.fog}`,
  },
  sectionLabel: {
    fontSize: fontSize.xs,
    fontWeight: fontWeight.medium,
    color: colors.stone,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    marginBottom: space.md,
  },
  fieldGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: space.md,
  },
  label: {
    display: "flex",
    flexDirection: "column",
    gap: space.xs,
    fontSize: fontSize.sm,
  },
  labelText: {
    color: colors.stone,
    fontWeight: fontWeight.medium,
  },
  required: { color: colors.error },
  input: {
    padding: space.sm,
    fontSize: fontSize.base,
    border: `1px solid ${colors.fog}`,
    borderRadius: radius.sm,
    background: colors.white,
    color: colors.ink,
    fontFamily: "inherit",
  },
  readOnlyBox: {
    padding: space.sm,
    background: colors.paper,
    border: `1px solid ${colors.fog}`,
    borderRadius: radius.sm,
    fontSize: fontSize.base,
    color: colors.ink,
  },
  readOnlyHint: {
    fontSize: fontSize.sm,
    color: colors.stone,
  },
  radioGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: space.sm,
  },
  radioCard: {
    display: "flex",
    flexDirection: "column",
    gap: space.xs,
    padding: space.md,
    border: `1px solid ${colors.fog}`,
    borderRadius: radius.sm,
    cursor: "pointer",
    background: colors.white,
  },
  radioCardActive: {
    border: `1px solid ${colors.indigo}`,
    background: colors.indigoSoft,
  },
  radioTitle: {
    fontSize: fontSize.base,
    fontWeight: fontWeight.medium,
    color: colors.ink,
  },
  radioHelp: {
    fontSize: fontSize.sm,
    color: colors.stone,
  },
  checkboxRow: {
    display: "inline-flex",
    alignItems: "center",
    gap: space.sm,
    fontSize: fontSize.sm,
    color: colors.ink,
    cursor: "pointer",
  },
  empty: {
    padding: space.lg,
    color: colors.stone,
    fontSize: fontSize.sm,
    textAlign: "center",
  },
  errorBox: {
    color: colors.error,
    fontSize: fontSize.sm,
    marginTop: space.sm,
    background: colors.errorSoft,
    padding: space.sm,
    borderRadius: radius.sm,
  },
  formActions: {
    display: "flex",
    gap: space.sm,
    justifyContent: "flex-end",
    marginTop: space.lg,
  },
  cancelBtn: {
    padding: `${space.sm}px ${space.lg}px`,
    border: `1px solid ${colors.fog}`,
    background: "transparent",
    borderRadius: radius.sm,
    color: colors.ink,
    fontSize: fontSize.base,
    fontWeight: fontWeight.medium,
    cursor: "pointer",
  },
  submitBtn: {
    padding: `${space.sm}px ${space.lg}px`,
    background: colors.indigo,
    color: colors.white,
    border: "none",
    borderRadius: radius.sm,
    fontSize: fontSize.base,
    fontWeight: fontWeight.medium,
    cursor: "pointer",
  },
};
